/* ---------------------------------------------------------------------------
 * Author: Akarsh Singh
 * Date: 2026-08-19
 * Description: Carry the RFQ priority bands through from the assistant reply so
 *   the chat panel can paint them. The written answer names the colours, but a
 *   chat bubble renders plain text, so a supplier reading "one red" saw the same
 *   black type as everything else. The bands now travel as data.
 * ------------------------------------------------------------------------- */

import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { WebApiService } from './web-api.service';
import { CookieService } from 'ngx-cookie-service';

export type ChatbotRole = 'system' | 'assistant' | 'user';

/** One colour band of the RFQ priority queue, as the RFQ list screen paints it. */
export interface RfqPriorityBand {
  tier: 'critical' | 'urgent' | 'normal';
  colour: 'red' | 'amber' | 'green';
  heading: string;
  note: string;
  rfqs: Array<{
    reference: string;
    buyer: string | null;
    timeLeft: string | null;
    vessel: string | null;
    port: string | null;
  }>;
}

export interface RfqPriorityBands {
  bands: RfqPriorityBand[];
  quoteWindowAlreadyClosed: number | null;
  stillAwaitingYourQuote: number | null;
}

/** One predicted requirement, as the Suggested RFQs screen lists it. */
export interface RfqPredictionRow {
  productName: string;
  partNumber: string | null;
  category: string | null;
  unit: string | null;
  vesselName: string | null;
  vesselImoNumber: string | null;
  purchases: number | null;
  averageQuantity: number | null;
  averageGapDays: number | null;
  predictedNextRequirement: string | null;
  /** The predicted date has already passed, so the requirement is due now. */
  overdue: boolean;
  frequency: string | null;
  confidence: string | null;
}

export interface RfqPredictions {
  kind: 'product' | 'vessel';
  rows: RfqPredictionRow[];
  totalRecords: number;
}

/** Which kind of RFQ a buyer is reviewing quotation history for. */
export type QuoteHistoryRfqType = 'product' | 'service';

/** One quotation version a supplier submitted, as the Quote History screen
 *  lists it. */
export interface QuoteHistoryVersionRow {
  versionNo: number;
  quoteNumber: string | null;
  submittedOn: string | null;
  totalAmount: number | null;
  currency: string;
  deliveryDays: number | null;
  paymentTerms: string | null;
  status: string | null;
  /** Change against the previous version, in percent; negative is cheaper. */
  changePercent: number | null;
}

/** One supplier's negotiation on one RFQ — the versions panel of the Quote
 *  History screen (operations/rfq-quote-history). */
export interface QuoteHistoryDetail {
  rfqType: QuoteHistoryRfqType;
  rfqRefNumber: string;
  vesselName: string | null;
  poNumber: string | null;
  rfqDate: string | null;
  supplierName: string;
  /** Latest version first. */
  versions: QuoteHistoryVersionRow[];
  /** First version against the accepted one, or the latest when none has been
   *  accepted yet — the same basis the screen's Excel export uses. Positive
   *  amount means the price came down. */
  savings: {
    amount: number;
    percent: number;
    currency: string;
    fromVersion: number;
    toVersion: number;
    accepted: boolean;
  } | null;
}

/** One choice the assistant offers as a button inside its own bubble. */
export interface ChatbotOption {
  label: string;
  value: string;
  icon?: string;
  description?: string;
}

/** What a set of option buttons is choosing, so the panel knows what to do
 *  with the click. */
export type ChatbotOptionsFor = 'quote-history-rfq-type' | 'quote-history-rfq' | 'quote-history-supplier';

export interface ChatbotMessage {
  id: string;
  role: ChatbotRole;
  content: string;
  timestamp: Date;
  tone?: 'info' | 'success' | 'warning';
  cta?: 'notifications' | 'products';
  steps?: string[];
  link?: { label: string; route: any[]; queryParams?: Record<string, any> };
  /** True when the answer was built from the user's real data rather than
   *  general knowledge. Drives the "From your live data" marker. */
  dataBacked?: boolean;
  /** Deep links to the screens behind the data the answer used. */
  links?: Array<{ label: string; route: any[]; queryParams?: Record<string, any> }>;
  /** RFQ priority bands, painted in the same red / amber / green the RFQ list
   *  screen uses. The written answer names the colours, but a chat bubble is
   *  plain text and cannot show one — so the bands arrive as data and the
   *  panel draws them. Absent on every answer except the priority queue. */
  rfqPriority?: RfqPriorityBands;
  /** Product / vessel RFQ predictions from the buyer's PO history — the same
   *  rows the Suggested RFQs screen shows. Absent on every other answer. */
  rfqPredictions?: RfqPredictions;
  /** Choices drawn as buttons under the answer — e.g. Product / Service RFQ,
   *  the RFQs a search matched, or the suppliers who quoted an RFQ. */
  options?: ChatbotOption[];
  optionsFor?: ChatbotOptionsFor;
  /** The search the options belong to, carried on the message so a pick can
   *  repeat it without the panel keeping state of its own. */
  optionContext?: { rfqType: QuoteHistoryRfqType; searchTerm: string; rfqId?: string };
  /** Value of the option already picked; the buttons lock once set. */
  selectedOption?: string;
  /** One supplier's quotation versions and negotiated savings on one RFQ.
   *  Absent on every answer except a finished quote-history lookup. */
  quoteHistory?: QuoteHistoryDetail;
  /** The assistant ran out of time or the model failed, so this is a plainer
   *  answer built from data already fetched. */
  degraded?: boolean;
  /** Show a retry affordance. Absent for failures only an admin can fix. */
  canRetry?: boolean;
  /** True when only an administrator can resolve this — a revoked key, no
   *  credit, a retired model. The UI says so instead of inviting a retry. */
  needsAdministrator?: boolean;
  /** Safe label for the failure, e.g. 'invalid_key'. Never the vendor's raw
   *  text, which can contain a partially masked API key. */
  failureKind?: string;
  /** Step-by-step timing for this answer, shown in the expandable breakdown. */
  timing?: {
    totalMs: number;
    roundTripMs: number;
    networkMs: number;
    beforeAgentMs?: number;
    startupMeasurementUnreliable?: boolean;
    coldStart: boolean;
    containerStartupMs?: number | null;
    steps: Array<{ label: string; milliseconds: number }>;
  };
  /** Whether the user has expanded the timing breakdown on this message. */
  showTiming?: boolean;
}

export interface QuickPrompt {
  id: string;
  label: string;
  prompt: string;
  /** Material icon name for the action chip */
  icon?: string;
  /** One-line hint shown under the label */
  description?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ChatbotAiService {
  constructor(private webApiService: WebApiService, private cookieService: CookieService) {}

  /**
   * Wakes up the AI backend so the user's first question does not have to wait
   * for a cold start.
   *
   * The AI Lambda loads the entire API on start-up, which takes several
   * seconds, and we cannot pre-warm it from the infrastructure side. So the
   * chat panel calls this the moment it opens: the backend starts up while the
   * user is still reading the greeting and typing, instead of after they press
   * send.
   *
   * Deliberately fire-and-forget. It must never block the UI, never show a
   * spinner and never surface an error — if the warm-up fails the assistant
   * still works, it is just slower on the first question.
   *
   * Author: Akarsh Singh - 2026-08-12
   */
  warmUpAiBackend(): void {
    this.webApiService.postRequest('/ai/ping', {}).catch(() => {
      // Intentionally ignored. A failed warm-up is not a user-facing problem.
    });
  }

  /** Lets other components (e.g. the Help Center panel) pop the chatbot open.
   *  The chatbot component subscribes to this and sets itself visible. */
  private openRequested = new Subject<void>();
  openRequested$ = this.openRequested.asObservable();
  requestOpen(): void {
    this.openRequested.next();
  }

  // Role-scoped welcome messages. The chatbot adapts to who is logged in:
  // buyers see procurement-side help, suppliers see fulfilment-side help,
  // admins see platform-ops help. No cross-role concepts in either flow.
  private welcomeByRole: Record<string, ChatbotMessage[]> = {
    buyer: [
      {
        id: 'welcome-buyer',
        role: 'assistant',
        content:
          "Hi! I'm VSMPS-AI — your marine procurement copilot. I'll help you with the BUYER cycle: creating RFQs, comparing quotes, approving POs, tracking deliveries, paying invoices, and analysing spend.",
        timestamp: new Date(),
        tone: 'info',
      },
      {
        id: 'hint-buyer',
        role: 'assistant',
        content:
          'Try "Show me POs waiting for approval", "Compare quotes for RFQ #1234", or "Which suppliers had the best on-time delivery this quarter?"',
        timestamp: new Date(),
        tone: 'info',
      },
    ],
    supplier: [
      {
        id: 'welcome-supplier',
        role: 'assistant',
        content:
          "Hi! I'm VSMPS-AI — your marine fulfilment copilot. I'll help you with the SUPPLIER cycle: responding to RFQs, submitting quotes, accepting POs, marking deliveries, tracking payments, and maintaining your catalog.",
        timestamp: new Date(),
        tone: 'info',
      },
      {
        id: 'hint-supplier',
        role: 'assistant',
        content:
          'Try "Show my open RFQs", "How do I mark a PO as delivered?", or "Summarise my performance this month".',
        timestamp: new Date(),
        tone: 'info',
      },
    ],
    admin: [
      {
        id: 'welcome-admin',
        role: 'assistant',
        content:
          "Hi! I'm VSMPS-AI — your platform copilot. I'll help with the ADMIN cycle: invoice management (€300/vessel buyers, 1% suppliers), user management, supplier onboarding, and system health.",
        timestamp: new Date(),
        tone: 'info',
      },
      {
        id: 'hint-admin',
        role: 'assistant',
        content:
          'Try "Open invoice management", "Show me all suppliers awaiting onboarding", or "Give me a platform overview".',
        timestamp: new Date(),
        tone: 'info',
      },
    ],
    guest: [
      {
        id: 'welcome-guest',
        role: 'assistant',
        content:
          "Hi! I'm VSMPS-AI. Please log in so I can tailor procurement assistance to your role (buyer, supplier, or admin).",
        timestamp: new Date(),
        tone: 'info',
      },
    ],
  };

  // Role-specific suggestion chips that sit above the action grid. These
  // replace the previous generic "Dashboard overview" / "Recent notifications"
  // chips so a buyer never sees supplier-side prompts and vice versa.
  private suggestionChipsByRole: Record<string, QuickPrompt[]> = {
    buyer: [
      { id: 'overview',      label: 'My dashboard',       prompt: 'Give me an overview of my procurement dashboard.' },
      { id: 'notifications', label: 'Notifications',      prompt: 'Summarise my recent procurement notifications.' },
    ],
    supplier: [
      { id: 'qa-performance', label: 'My performance',    prompt: 'Summarise my supplier performance this month.' },
      { id: 'notifications',  label: 'Notifications',     prompt: 'Summarise my recent notifications.' },
    ],
    admin: [
      { id: 'qa-admin-overview', label: 'System overview',  prompt: 'Give me a platform health and KPI overview.' },
      { id: 'notifications',     label: 'Notifications',    prompt: 'Summarise admin notifications.' },
    ],
    guest: [],
  };

  private seedMessages: ChatbotMessage[] = []; // populated dynamically by getInitialMessages()

  // The scriptedResponses block that used to live here has been DELETED.
  // It contained invented figures — "Dashboard snapshot: 10 RFQs created, 3
  // orders placed, and 5 POs issued in the past week" — which were rendered as
  // an ordinary assistant reply whenever an API call failed. A user had no way
  // to tell them from real data. Failures now say so plainly instead.
  // Author: Akarsh Singh - 2026-08-12

  private quickPrompts: QuickPrompt[] = [
    {
      id: 'overview',
      label: 'Dashboard overview',
      prompt: 'Give me an overview of procurement analytics .',
    },
    {
      id: 'notifications',
      label: 'Recent notifications',
      prompt: 'What are my recent notifications?',
    },
    
  ];

  private quickActionsByRole: Record<string, QuickPrompt[]> = {
    supplier: [
      { id: 'qa-respond-rfq',     label: 'Open RFQs',         description: 'Quote unactioned RFQs',           icon: 'mark_email_unread', prompt: 'Help me respond to my latest RFQs.' },
      { id: 'qa-view-pos',        label: 'View POs',          description: 'See incoming purchase orders',    icon: 'assignment_turned_in', prompt: 'Show me my purchase orders.' },
      { id: 'qa-update-catalog',  label: 'Update Catalog',    description: 'Edit products & pricing',         icon: 'inventory_2', prompt: 'Show me steps to update my product catalog.' },
      { id: 'qa-mark-delivered',  label: 'Mark Delivered',    description: 'Confirm shipments delivered',     icon: 'local_shipping', prompt: 'How do I mark an order as delivered?' },
      { id: 'qa-view-payments',   label: 'Payments',          description: 'Track invoice payments',          icon: 'payments', prompt: 'Show me my recent payments.' },
      { id: 'qa-rfq-coverage',    label: 'Check RFQ Fit',     description: 'Can I supply this RFQ?',          icon: 'fact_check', prompt: 'Can I supply this RFQ?' },
      { id: 'qa-performance',     label: 'Performance',       description: 'My fulfilment metrics',           icon: 'trending_up', prompt: 'Summarize my current performance metrics.' },
      { id: 'qa-compliance',      label: 'Compliance',        description: 'Sustainability & certs',          icon: 'verified', prompt: 'List my pending compliance tasks.' },
    ],
    buyer: [
      { id: 'qa-create-rfq',                  label: 'Create RFQ',                    description: 'New procurement request',                icon: 'add_circle',      prompt: 'Help me create a new RFQ.' },
      { id: 'qa-compare-quotes',              label: 'Compare Quotes',                description: 'Pick the best supplier',                 icon: 'compare_arrows',  prompt: 'Help me compare supplier quotes.' },
      { id: 'qa-approve-po',                  label: 'Approve POs',                   description: 'Pending PO approvals',                   icon: 'fact_check',      prompt: 'Show me POs waiting for approval.' },
      { id: 'qa-track-order',                 label: 'Track Delivery',                description: 'Monitor order shipments',                icon: 'local_shipping',  prompt: 'Track my latest order status.' },
      { id: 'qa-view-invoices',               label: 'View Invoices',                 description: 'Recent invoices',                        icon: 'receipt_long',    prompt: 'Show me my recent invoices.' },
      { id: 'qa-my-suppliers',                label: 'My Suppliers',                  description: 'Manage supplier relationships',          icon: 'storefront',      prompt: 'Show me my suppliers.' },
      { id: 'qa-analyze-spend',               label: 'Spend Analytics',               description: 'Spend by category',                      icon: 'pie_chart',       prompt: 'Analyze my spending by category.' },
      { id: 'qa-evaluate-suppliers',          label: 'Supplier Scores',               description: 'Performance evaluation',                 icon: 'leaderboard',     prompt: 'Evaluate my suppliers performance.' },
      { id: 'qa-product-rfq-predictions',     label: 'Product RFQ Predictions',       description: 'Predict upcoming product requirements',  icon: 'auto_awesome',    prompt: 'Show me my product RFQ predictions.' },
      { id: 'qa-vessel-product-predictions',  label: 'Vessel RFQ Predictions',        description: 'Predict product requirements by vessel', icon: 'directions_boat', prompt: 'Show me vessel-wise product RFQ predictions.' },
      { id: 'qa-quote-history',               label: 'Quote History',                 description: 'Negotiation & savings',                  icon: 'history',         prompt: 'Show me the quotation history and negotiated savings.' },
    ],
    admin: [
      { id: 'qa-admin-invoices',  label: 'Invoice Mgmt',     description: 'Generate buyer/supplier invoices', icon: 'request_quote', prompt: 'Open invoice management.' },
      { id: 'qa-admin-users',     label: 'User Management',  description: 'View all platform users',          icon: 'manage_accounts', prompt: 'Show me user management.' },
      { id: 'qa-admin-suppliers', label: 'All Suppliers',    description: 'Platform supplier directory',      icon: 'storefront', prompt: 'Show me all suppliers on the platform.' },
      { id: 'qa-admin-overview',  label: 'System Overview',  description: 'Platform health & KPIs',           icon: 'dashboard', prompt: 'Give me a system overview.' },
    ],
  };

  /** Optional context (e.g., the current route URL) — set by the component so
   *  scripted/AI responses can be page-aware. */
  private routeContext: string | undefined;
  setRouteContext(url?: string) { this.routeContext = url; }
  getRouteContext(): string | undefined { return this.routeContext; }

  /** Lightweight conversation history sent to the backend so multi-turn
   *  follow-ups stay coherent. Kept in memory only; resets on page reload. */
  private conversationHistory: Array<{ role: 'user' | 'assistant'; content: string }> = [];

  /** Tools the assistant used on the last answer. Sent with the next question so
   *  a follow-up like "and which is due soonest?" stays on the same subject. */
  private toolNamesUsedEarlier: string[] = [];
  private readonly maxHistory = 12;

  pushHistory(role: 'user' | 'assistant', content: string) {
    this.conversationHistory.push({ role, content });
    if (this.conversationHistory.length > this.maxHistory) {
      this.conversationHistory.splice(0, this.conversationHistory.length - this.maxHistory);
    }
  }

  /** Marine procurement domain glossary — passed to the backend as part of
   *  the system context so the AI grounds its answers in the platform's
   *  vocabulary instead of generic procurement. */
  private readonly marineGlossary = {
    domain: 'marine-procurement',
    platform: 'VSMPS (Varuna Sentinels Maritime Procurement System)',
    keyTerms: {
      RFQ: 'Request for Quotation — marine procurement request issued by a buyer (shipowner/ship-manager) to one or more suppliers/chandlers',
      Quote: 'Supplier response to an RFQ with prices, delivery dates, and terms',
      PO: 'Purchase Order — buyer-issued order following quote acceptance',
      Invoice: 'Billing document — VSMPS charges €300/vessel/year to buyers and 1% commission to suppliers',
      IMPA: 'International Marine Purchasing Association — standard 6-digit code system for ship spares/consumables',
      Vessel: 'Ship registered to the buyer (tanker/bulker/container/etc.) — RFQs are typically tied to a vessel',
      POD: 'Proof of Delivery — required when a supplier marks a PO delivered',
      CMR: 'International road-freight consignment note — sometimes used as POD',
      DraftSurvey: 'Survey performed prior to/after loading; not directly tracked here',
      ChandlerSupplier: 'Marine chandler — sells consumables, spares, provisions to vessels at ports',
      Lifecycle: 'RFQ → Quoted → Accepted Quote → PO Sent → Accepted PO → In Transit → Delivered → Invoiced → Paid',
      SupplierTypes: 'Engineering, Electrical, Safety, Hotel & Provisions, Deck, Bridge, Chemical, Spare Parts',
    },
    cycleByRole: {
      buyer:    'Create RFQ → Receive quotes → Compare → Award → Issue PO → Approve → Receive delivery → Pay invoice',
      supplier: 'Receive RFQ → Submit quote → Receive PO → Acknowledge → Ship → Mark delivered → Receive payment',
      admin:    'Manage users → Approve supplier onboarding → Generate invoices (€300/vessel buyer, 1% supplier) → Monitor platform health',
    },
  };

  /** Build the structured request body sent to backend AI endpoints. Shaped
   *  to be drop-in compatible with Anthropic Claude /v1/messages — backend
   *  just needs to forward `system` + `messages[]` to the upstream call.
   *
   *  See docs/superpowers/specs/2026-05-28-chatbot-claude-backend-spec.md. */
  buildAiRequest(question: string, promptId?: string): any {
    const role = this.getUserRole();
    const roleLabel = this.getRoleLabel();
    const cycleSummary = role && (this.marineGlossary.cycleByRole as any)[role]
      ? (this.marineGlossary.cycleByRole as any)[role]
      : 'general procurement workflow';

    const system = [
      `You are VSMPS-AI, an assistant embedded in the Varuna Sentinels Maritime Procurement System (VSMPS).`,
      `The user is signed in as ${roleLabel}. Stay strictly within the ${roleLabel} cycle: ${cycleSummary}.`,
      `If the user asks about a task that belongs to a different role, decline politely and remind them which role owns it. Do NOT generate guidance for actions outside their cycle.`,
      `Domain: ${this.marineGlossary.platform}. Use marine procurement vocabulary (RFQ, PO, IMPA, vessel, POD/CMR, chandler, supplier types).`,
      `Be concise (under 120 words), specific to maritime procurement, and reference real platform features when relevant.`,
      this.routeContext ? `User is currently on the page: ${this.routeContext}.` : '',
    ].filter(Boolean).join(' ');

    const messages: Array<{ role: 'user' | 'assistant'; content: string }> = [
      ...this.conversationHistory,
      { role: 'user', content: question },
    ];

    return {
      role,
      domain: this.marineGlossary.domain,
      route: this.routeContext,
      promptId,
      system,
      messages,
      glossary: this.marineGlossary,
      question, // keep for legacy backends not yet on the new payload shape
    };
  }

  getInitialMessages(): ChatbotMessage[] {
    const role = this.getUserRole() || 'guest';
    const list = this.welcomeByRole[role] || this.welcomeByRole['guest'];
    // Stamp each with a current timestamp so the order is deterministic.
    return list.map((m) => ({ ...m, timestamp: new Date() }));
  }

  /** Role-aware suggestion chips (replaces the legacy flat list). */
  getQuickPrompts(role?: string): QuickPrompt[] {
    const resolved = (role || this.getUserRole() || 'guest').toLowerCase();
    const list = this.suggestionChipsByRole[resolved] || this.suggestionChipsByRole['guest'];
    return list.map((p) => ({ ...p }));
  }

  /** True if the given action id belongs to the given role (or is global). */
  private isActionInRoleScope(promptId: string | undefined, role: string | undefined): boolean {
    if (!promptId) return true;
    if (promptId === 'overview' || promptId === 'notifications') return true; // shared core
    const resolvedRole = (role || this.getUserRole() || '').toLowerCase();
    const actions = this.quickActionsByRole[resolvedRole] || [];
    return actions.some((a) => a.id === promptId);
  }

  /** Detect cross-role intent in free-form input so we can refuse rather than
   *  silently mislead. Each entry maps a keyword to the role that owns it. */
  private readonly crossRoleKeywords: Array<{ pattern: RegExp; ownedBy: 'buyer' | 'supplier' | 'admin' }> = [
    // Buyer-only
    { pattern: /\b(create|draft|new)\s+rfq\b/i,        ownedBy: 'buyer' },
    { pattern: /\bapprove\s+po\b/i,                    ownedBy: 'buyer' },
    { pattern: /\bcompare\s+(quotes?|suppliers?)\b/i,  ownedBy: 'buyer' },
    { pattern: /\bspend(ing)?\s+analytics?\b/i,        ownedBy: 'buyer' },
    { pattern: /\bmy\s+suppliers?\b/i,                 ownedBy: 'buyer' },
    // Supplier-only
    { pattern: /\brespond\s+to\s+rfq\b/i,              ownedBy: 'supplier' },
    { pattern: /\bsubmit\s+quote\b/i,                  ownedBy: 'supplier' },
    { pattern: /\bupdate\s+(my\s+)?catalog\b/i,        ownedBy: 'supplier' },
    { pattern: /\bmark\s+(as\s+)?delivered\b/i,        ownedBy: 'supplier' },
    { pattern: /\b(my\s+)?compliance\b/i,              ownedBy: 'supplier' },
    // Admin-only
    { pattern: /\binvoice\s+management\b/i,            ownedBy: 'admin' },
    { pattern: /\buser\s+management\b/i,               ownedBy: 'admin' },
    { pattern: /\bsystem\s+overview\b/i,               ownedBy: 'admin' },
    { pattern: /\bplatform\s+(health|kpi)\b/i,         ownedBy: 'admin' },
  ];

  /** Returns a refusal message if the question targets a different role's
   *  cycle, otherwise undefined. */
  detectCrossRoleIntent(question: string): ChatbotMessage | undefined {
    const role = (this.getUserRole() || '').toLowerCase();
    if (!role) return undefined;
    for (const { pattern, ownedBy } of this.crossRoleKeywords) {
      if (pattern.test(question) && ownedBy !== role) {
        return {
          id: `refusal-${Date.now()}`,
          role: 'assistant',
          content: this.refusalCopy(role, ownedBy),
          timestamp: new Date(),
          tone: 'warning',
        };
      }
    }
    return undefined;
  }

  private refusalCopy(currentRole: string, ownedBy: 'buyer' | 'supplier' | 'admin'): string {
    const currentLabel = currentRole === 'buyer' ? 'Buyer' : currentRole === 'supplier' ? 'Supplier' : currentRole === 'admin' ? 'Admin' : 'Guest';
    const ownedLabel = ownedBy === 'buyer' ? 'Buyer' : ownedBy === 'supplier' ? 'Supplier' : 'Admin';
    return `That action belongs to the ${ownedLabel} cycle — you're signed in as ${currentLabel}, so I'll keep my help focused on ${currentLabel}-side tasks. If you need ${ownedLabel} actions, switch to a ${ownedLabel} account.`;
  }

  getQuickActions(role?: string): QuickPrompt[] {
    const resolvedRole = (role || this.getUserRole())?.toLowerCase();
    const actions = resolvedRole && this.quickActionsByRole[resolvedRole] ? this.quickActionsByRole[resolvedRole] : [];
    return actions.map((prompt) => ({ ...prompt }));
  }

  /**
   * Quick actions that are pure guidance or navigation.
   *
   * These stay as written text on purpose. They are instant, they cost nothing,
   * and the steps are verified against the real screens — so sending them to a
   * language model would make them slower and less certain, not better. Only
   * questions that need the user's actual DATA go to the assistant.
   */
  private readonly guidanceActions: Record<
    string,
    { content: string; steps?: string[]; link?: { label: string; route: any[]; queryParams?: Record<string, any> } }
  > = {
    'qa-mark-delivered': {
      content: 'To confirm a delivery and trigger invoicing:',
      steps: [
        'Open your PO list',
        'Filter to "Accepted" or "In Transit" orders',
        'Click the PO',
        'Use Update Status → Delivered',
        'Attach POD/CMR if required, then save',
      ],
      link: {
        label: 'Open PO list (Accepted)',
        route: ['/operations/supplier-purchase-order-list'],
        queryParams: { selectedStatus: 'Accepted', label: 'Order' },
      },
    },
    'qa-compliance': {
      content:
        'Manage compliance and sustainability: review required certifications, add your sustainability information to your profile, and update catalog items accordingly.',
      link: {
        label: 'Go to suppliers with sustainability info',
        route: ['/search/products'],
        queryParams: { supplierType: 1 },
      },
    },
    'qa-create-rfq': {
      content: 'Here is the fast path to create a new RFQ:',
      steps: [
        'Go to Product page and add items to cart',
        'Open the cart and click Create RFQ',
        'Complete Line Items',
        'Fill Header tab',
        'Set Payment Terms',
        'Preview, then Send RFQ or Save as draft',
      ],
    },
    'qa-compare-quotes': {
      content: 'Here is how to compare incoming quotes and pick the best supplier:',
      steps: [
        'Open your RFQ list',
        'Pick an RFQ in "Quoted" status',
        'Click the Compare Quotes action',
        'Review price, delivery, and supplier score side by side',
        'Select the winning quote and convert to PO',
      ],
      link: {
        label: 'Open RFQ list (Quoted)',
        route: ['/operations/buyer-rfq-list'],
        queryParams: { selectedStatus: 'Quoted', label: 'RFQ' },
      },
    },
    'qa-approve-po': {
      content: 'POs awaiting your approval:',
      steps: [
        'Open Purchase Orders',
        'Filter to "Pending Approval" status',
        'Click a PO to review line items, pricing, and supplier details',
        'Hit Approve or Reject — approval routes the PO to the supplier',
      ],
      link: {
        label: 'Go to POs pending approval',
        route: ['/operations/purchase-order-list'],
        queryParams: { selectedStatus: 'Pending Approval', label: 'Order' },
      },
    },
    'qa-track-order': {
      content: 'Open your invoice list to track order and payment status.',
      link: { label: 'Go to invoice list', route: ['/operations/invoice-list'] },
    },
    'qa-view-invoices': {
      content:
        'Here are your recent invoices. From the list you can view PDFs, raise disputes, and track payment status.',
      link: { label: 'Open invoice list', route: ['/operations/invoice-list'] },
    },
    'qa-emergency-order': {
      content: 'Opening your purchase orders filtered to sent status for urgent handling.',
      link: {
        label: 'Go to purchase orders',
        route: ['/operations/purchase-order-list'],
        queryParams: { docStatus: 'Sent', selectedStatus: 'Sent', label: 'Order' },
      },
    },
    'qa-admin-invoices': {
      content:
        'Admin Invoice Management — generate buyer (€300/vessel) and supplier (1%) invoices, preview PDFs, and dispatch via SES.',
      link: {
        label: 'Open Invoice Management',
        route: ['/dashboard/admin-dashboard'],
        queryParams: { tab: 'invoicing' },
      },
    },
    'qa-admin-users': {
      content: 'Platform user management — view roles, activate/deactivate accounts, and audit activity.',
      link: { label: 'Open User Management', route: ['/administration/userMaster'] },
    },
    'qa-admin-suppliers': {
      content: 'All suppliers on the platform — directory, onboarding status, and compliance overview.',
      link: { label: 'Open Suppliers', route: ['/administration/suppliersList'] },
    },
    'qa-admin-overview': {
      content: 'Admin dashboard — platform-wide KPIs, recent activity, and system health.',
      link: { label: 'Open Admin Dashboard', route: ['/dashboard/admin-dashboard'] },
    },
  };

  /**
   * Quick actions that need the user's real data, and so go to the assistant.
   *
   * The text is the question the assistant is asked. Phrasing matters: it is
   * what the retriever scores, so it should read the way a supplier would
   * actually ask.
   */
  private readonly dataActionQuestions: Record<string, string> = {
    overview: 'Give me a summary of my business — RFQs, quotes, orders and revenue.',
    notifications: 'What are my recent notifications?',
    'qa-performance': 'How am I performing? Show my ratings, quote accuracy and delivery performance.',
    'qa-view-pos': 'Show me my purchase orders.',
    'qa-view-payments': 'Show me my invoices and which ones are unpaid.',
  };

  /**
   * Guidance for the two screens whose route includes the user's own ids.
   *
   * The catalog and My Suppliers screens are addressed as
   * /dataManagement/<screen>/<userId>/<companyId>, so their links cannot live in
   * the static guidance map — they have to be built from the cookies at click
   * time. Falls back to the bare route when either id is missing, which is what
   * the previous implementation did too.
   *
   * Returns undefined when the prompt is not one of these two, so the caller
   * carries on to the normal routing.
   */
  private buildUserScopedGuidance(promptId?: string): ChatbotMessage | undefined {
    if (promptId !== 'qa-update-catalog' && promptId !== 'qa-my-suppliers') {
      return undefined;
    }

    const userIdFromCookie = Number(this.cookieService.get('userId')) || undefined;
    const companyIdFromCookie = Number(this.cookieService.get('companyId')) || undefined;
    const screenPath =
      promptId === 'qa-update-catalog' ? '/dataManagement/products' : '/dataManagement/my-supplier';

    const route =
      userIdFromCookie && companyIdFromCookie
        ? [screenPath, userIdFromCookie, companyIdFromCookie]
        : [screenPath];

    if (promptId === 'qa-update-catalog') {
      return this.buildAssistantMessage({
        content:
          'To update your catalog: open Catalog Management, add or edit products, assign categories and attributes, set pricing and availability, then save. You can bulk upload if that is enabled, or manage items one at a time.',
        link: { label: 'Go to catalog', route },
      });
    }

    return this.buildAssistantMessage({
      content: 'Your supplier directory. You can score, group, and onboard suppliers from here.',
      link: { label: 'Open My Suppliers', route },
    });
  }

  /**
   * Builds an assistant message from whatever the caller has.
   *
   * Kept in one place so every reply — from the assistant, from guidance, or
   * from a failure — is shaped identically and the template never has to guess
   * which fields exist.
   */
  private buildAssistantMessage(fields: Partial<ChatbotMessage>): ChatbotMessage {
    return {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      tone: 'info',
      ...fields,
    } as ChatbotMessage;
  }

  /**
   * Asks the tool-calling assistant a question and turns the reply into a message.
   *
   * The backend always answers 200, even when something failed, so a thrown
   * error here means the network itself is down. In that case we say so plainly
   * rather than showing a made-up answer — the old scripted fallbacks used to
   * print invented figures like "10 RFQs created, 3 orders placed", which a user
   * had no way of telling apart from real data.
   *
   * Author: Akarsh Singh - 2026-08-12
   */
  async askTheAssistant(question: string, extraBody?: Record<string, any>): Promise<ChatbotMessage> {
    // Measured on this side of the wire, so the difference between it and the
    // server's own total is the network and browser overhead — the part the
    // backend logs can never see.
    const requestSentAt = Date.now();

    try {
      const response: any = await this.webApiService.postRequest('/ai/agent', {
        question,
        route: this.routeContext,
        messages: this.conversationHistory,
        toolsUsedEarlier: this.toolNamesUsedEarlier,
        ...extraBody,
      });

      const roundTripMs = Date.now() - requestSentAt;
      const payload = response?.answer ? response : response?.data || {};

      // Remember which tools ran so a follow-up like "and which is due soonest?"
      // stays on the same subject.
      if (Array.isArray(payload.usedTools) && payload.usedTools.length) {
        this.toolNamesUsedEarlier = payload.usedTools.slice(-3);
      }

      return this.buildAssistantMessage({
        content: payload.answer || 'I could not put together an answer for that. Please try rephrasing it.',
        dataBacked: payload.dataBacked === true,
        degraded: payload.degraded === true,
        links: Array.isArray(payload.links) ? payload.links : [],
        tone: payload.degraded ? 'warning' : 'info',
        canRetry: payload.canRetry === true,
        needsAdministrator: payload.needsAdministrator === true,
        failureKind: payload.failureKind,
        rfqPriority: payload.rfqPriority || undefined,
        timing: this.buildTimingBreakdown(payload.timeline, roundTripMs),
      });
    } catch {
      // The backend answers 200 even when it fails internally, so reaching here
      // means the request never arrived — the browser is offline, or the API is
      // down. Worth retrying, unlike a revoked key.
      return this.buildAssistantMessage({
        content:
          'I could not reach the assistant just now. Please check your connection and try again.',
        tone: 'warning',
        dataBacked: false,
        canRetry: true,
        failureKind: 'unreachable',
      });
    }
  }

  /**
   * The opening step of Quote History: the buyer picks Product or Service RFQ
   * before searching, because the two live in separate tables.
   */
  buildQuoteHistoryRfqTypePrompt(): ChatbotMessage {
    return this.buildAssistantMessage({
      id: `quote-history-type-${Date.now()}`,
      content: 'Which quotation history would you like to review?',
      optionsFor: 'quote-history-rfq-type',
      options: [
        { label: 'Product RFQ', value: 'product', icon: 'inventory_2' },
        { label: 'Service RFQ', value: 'service', icon: 'build' },
      ],
    });
  }

  /**
   * Looks up quotation history for what the buyer typed — an RFQ number, vessel
   * name or PO number — using the same call the Quote History screen
   * (operations/rfq-quote-history) makes, so the chat shows exactly what that
   * screen shows.
   *
   * Narrows down one step at a time, the way the screen does:
   *   1. several RFQs matched      → the buyer picks one   (comes back with rfqId)
   *   2. several suppliers quoted  → the buyer picks one   (comes back with supplierId)
   *   3. one RFQ, one supplier     → the versions and negotiated savings
   * A step with a single candidate is skipped.
   */
  async searchQuoteHistory(
    rfqType: QuoteHistoryRfqType,
    searchTerm: string,
    selection: { rfqId?: string; supplierId?: string } = {},
  ): Promise<ChatbotMessage> {
    const rfqLabel = rfqType === 'service' ? 'service RFQ' : 'product RFQ';
    const screenLink = (extra: Record<string, any> = {}) => ({
      label: 'Open Quote History',
      route: ['/operations/rfq-quote-history'],
      queryParams: { rfqType, searchTerm, ...extra },
    });

    let rows: any[];
    try {
      const response: any = await this.webApiService.postRequest('/rfq/fetchRfqAndQuoteHistoryDataByBuyerId', {
        id: null,
        rfqType,
        searchBy: 'all',
        searchParams: searchTerm,
        docStatus: null,
        fromDate: null,
        toDate: null,
        limit: 10,
        offset: 0,
        isActive: 1,
      });
      rows = Array.isArray(response?.rows)
        ? response.rows
        : Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
    } catch {
      return this.buildAssistantMessage({
        content: `I could not load the ${rfqLabel} quotation history just now. Please try again.`,
        tone: 'warning',
        canRetry: true,
        link: screenLink(),
      });
    }

    if (!rows.length) {
      return this.buildAssistantMessage({
        content: `No ${rfqLabel}s match "${searchTerm}". Try the full RFQ number, vessel name or PO number — or switch between Product and Service RFQ.`,
        dataBacked: true,
        link: screenLink(),
      });
    }

    // Step 1 — which RFQ.
    const rfqIdOf = (row: any) => String(row.rfqId ?? row.id);
    const rfq = selection.rfqId
      ? rows.find((row) => rfqIdOf(row) === selection.rfqId)
      : rows.length === 1
      ? rows[0]
      : undefined;

    if (!rfq) {
      return this.buildAssistantMessage({
        content: `${rows.length} ${rfqLabel}s match "${searchTerm}". Which one would you like to review?`,
        dataBacked: true,
        optionsFor: 'quote-history-rfq',
        optionContext: { rfqType, searchTerm },
        options: rows.map((row) => {
          const supplierCount = this.groupQuotesBySupplier(row).length;
          const facts = [
            row.vesselName,
            row.rfqDate ? this.formatShortDate(row.rfqDate) : null,
            `${supplierCount} supplier${supplierCount === 1 ? '' : 's'}`,
          ].filter(Boolean);
          return {
            label: row.rfqRefNumber || 'RFQ',
            value: rfqIdOf(row),
            icon: 'description',
            description: facts.join(' · '),
          };
        }),
      });
    }

    // Step 2 — which supplier.
    const rfqRef = rfq.rfqRefNumber || searchTerm;
    const rfqLink = (supplierId?: string) =>
      screenLink({ searchTerm: rfqRef, ...(supplierId ? { supplierId } : {}) });
    const suppliers = this.groupQuotesBySupplier(rfq);

    if (!suppliers.length) {
      return this.buildAssistantMessage({
        content: `No supplier has submitted a quotation for ${rfqRef} yet.`,
        dataBacked: true,
        link: rfqLink(),
      });
    }

    const supplier = selection.supplierId
      ? suppliers.find((s) => s.supplierId === selection.supplierId)
      : suppliers.length === 1
      ? suppliers[0]
      : undefined;

    if (!supplier) {
      return this.buildAssistantMessage({
        content: `${suppliers.length} suppliers quoted ${rfqRef}. Please select the supplier whose quotation history you want to review.`,
        dataBacked: true,
        optionsFor: 'quote-history-supplier',
        optionContext: { rfqType, searchTerm, rfqId: rfqIdOf(rfq) },
        options: suppliers.map((s) => {
          const latest = s.quotes[0];
          const amount = this.toNumberOrNull(latest?.totalCost);
          const facts = [
            `${s.quotes.length} version${s.quotes.length === 1 ? '' : 's'}`,
            amount != null ? `${latest?.quoteCurrency || ''} ${this.formatAmount(amount)}`.trim() : null,
          ].filter(Boolean);
          return { label: s.supplierName, value: s.supplierId, icon: 'storefront', description: facts.join(' · ') };
        }),
        link: rfqLink(),
      });
    }

    // Step 3 — the versions.
    const detail = this.buildQuoteHistoryDetail(rfqType, rfq, supplier);
    const savings = detail.savings;
    const versionCount = detail.versions.length;
    let summary = `${supplier.supplierName} submitted ${versionCount} version${versionCount === 1 ? '' : 's'} for ${rfqRef}.`;
    if (savings && savings.amount > 0) {
      summary += ` Negotiated down by ${savings.currency} ${this.formatAmount(savings.amount)} (${savings.percent.toFixed(1)}%) from V${savings.fromVersion} to V${savings.toVersion}${savings.accepted ? ' (accepted)' : ''}.`;
    } else if (savings && savings.amount < 0) {
      summary += ` The price went up by ${savings.currency} ${this.formatAmount(-savings.amount)} (${(-savings.percent).toFixed(1)}%) from V${savings.fromVersion} to V${savings.toVersion}.`;
    } else if (versionCount > 1) {
      summary += ' The price has not changed between versions.';
    }

    return this.buildAssistantMessage({
      content: summary,
      dataBacked: true,
      quoteHistory: detail,
      link: rfqLink(supplier.supplierId),
    });
  }

  /**
   * Groups an RFQ's quotation rows by supplier, each supplier's quotes latest
   * version first — the same grouping the Quote History screen uses.
   */
  private groupQuotesBySupplier(rfq: any): Array<{ supplierId: string; supplierName: string; quotes: any[] }> {
    type SupplierQuotes = { supplierId: string; supplierName: string; quotes: any[] };
    const list: any[] = Array.isArray(rfq?.supplierQuotationList) ? rfq.supplierQuotationList : [];
    const bySupplier = new Map<string, SupplierQuotes>();

    for (const quote of list) {
      if (quote?.supplierId == null) continue;
      const supplierId = String(quote.supplierId);
      const entry: SupplierQuotes = bySupplier.get(supplierId) ?? {
        supplierId,
        supplierName: quote.supplierName || 'Unnamed supplier',
        quotes: [],
      };
      entry.quotes.push(quote);
      bySupplier.set(supplierId, entry);
    }

    const groups = Array.from(bySupplier.values());
    groups.forEach((g) => g.quotes.sort((a, b) => Number(b.versionNumber ?? 0) - Number(a.versionNumber ?? 0)));
    return groups;
  }

  /** Turns one supplier's quotes on one RFQ into the versions the panel draws. */
  private buildQuoteHistoryDetail(
    rfqType: QuoteHistoryRfqType,
    rfq: any,
    supplier: { supplierName: string; quotes: any[] },
  ): QuoteHistoryDetail {
    const versions: QuoteHistoryVersionRow[] = supplier.quotes.map((q, index) => ({
      versionNo: Number(q.versionNumber ?? supplier.quotes.length - index),
      quoteNumber: q.quoteRefNumber || null,
      submittedOn: q.quotationDate || null,
      totalAmount: this.toNumberOrNull(q.totalCost),
      currency: q.quoteCurrency || '',
      deliveryDays: this.toNumberOrNull(q.quotedDeliveryInDays),
      paymentTerms: q.paymentTermsName || null,
      status: q.buyerQuoteStatus || null,
      changePercent: null,
    }));

    // Latest first, so the previous version is the next row down.
    versions.forEach((v, i) => {
      const previous = versions[i + 1]?.totalAmount;
      if (v.totalAmount != null && previous) {
        v.changePercent = ((v.totalAmount - previous) / previous) * 100;
      }
    });

    let savings: QuoteHistoryDetail['savings'] = null;
    if (versions.length > 1) {
      const first = versions[versions.length - 1];
      const accepted = versions.find((v) => /accept/i.test(v.status || ''));
      const final = accepted ?? versions[0];
      if (first.totalAmount && final.totalAmount != null && final !== first) {
        const amount = first.totalAmount - final.totalAmount;
        savings = {
          amount,
          percent: (amount / first.totalAmount) * 100,
          currency: final.currency || first.currency,
          fromVersion: first.versionNo,
          toVersion: final.versionNo,
          accepted: !!accepted,
        };
      }
    }

    return {
      rfqType,
      rfqRefNumber: rfq.rfqRefNumber || '—',
      vesselName: rfq.vesselName || null,
      poNumber: rfq.poNumber || null,
      rfqDate: rfq.rfqDate || null,
      supplierName: supplier.supplierName,
      versions,
      savings,
    };
  }

  private toNumberOrNull(value: any): number | null {
    const n = Number(value);
    return value == null || value === '' || isNaN(n) ? null : n;
  }

  private formatAmount(value: number): string {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private formatShortDate(value: any): string | null {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  /**
   * Turns the server's timeline into what the breakdown panel shows.
   *
   * The network row is the interesting one: it is the round trip measured in the
   * browser minus the time the server says it spent, so it isolates the part no
   * server log can see.
   *
   * Author: Akarsh Singh - 2026-08-12
   */
  private buildTimingBreakdown(
    serverTimeline: any,
    roundTripMs: number,
  ): ChatbotMessage['timing'] | undefined {
    if (!serverTimeline || !Array.isArray(serverTimeline.steps)) {
      return undefined;
    }

    const serverTotalMs = Number(serverTimeline.totalMs) || 0;

    // What the server did BEFORE the agent started — express routing, body
    // parsing and ensureAiAuth verifying the session. The server reports this
    // now; it used to be invisible and got charged to "Network", which made
    // work we control look like slow transport.
    const beforeAgentMs = Number(serverTimeline.beforeAgentMs) || 0;
    const serverAccountedMs = serverTotalMs + beforeAgentMs;

    return {
      totalMs: serverTotalMs,
      roundTripMs,
      beforeAgentMs,
      // Genuine transport and browser overhead only: everything the server has
      // accounted for is subtracted, not just the agent's own steps.
      // Never negative — clock skew or rounding can make the totals cross.
      networkMs: Math.max(0, roundTripMs - serverAccountedMs),
      coldStart: serverTimeline.coldStart === true,
      containerStartupMs: serverTimeline.containerStartupMs ?? null,
      startupMeasurementUnreliable:
        serverTimeline.startupMeasurementUnreliable === true,
      steps: serverTimeline.steps,
    };
  }

  /**
   * Decides how to answer one prompt, and produces the message.
   *
   * Three routes, in order:
   *   1. a guidance action  — instant written steps, no network call
   *   2. a data question    — the tool-calling assistant, which reads real data
   *   3. anything else      — the assistant too, so free-typed questions finally
   *                           get real answers instead of invented ones
   *
   * Buyers and admins still use the older endpoints, because the assistant's
   * tools are supplier-only for now and would otherwise refuse them.
   */
  async generateResponseFromApi(promptId?: string, question?: string): Promise<ChatbotMessage> {
    // Two screens are reached by a route that includes the signed-in user's own
    // ids, so their links have to be built at click time rather than sitting in
    // the static map above.
    const userScopedGuidance = this.buildUserScopedGuidance(promptId);
    if (userScopedGuidance) {
      return userScopedGuidance;
    }

    // RFQ predictions are read straight from the Suggested RFQs endpoint, so
    // the chat shows exactly the rows that screen shows.
    const predictionKind = this.resolvePredictionKind(promptId, question);
    if (predictionKind) {
      return this.buildRfqPredictionsMessage(predictionKind);
    }

    const guidance = promptId ? this.guidanceActions[promptId] : undefined;
    if (guidance) {
      return this.buildAssistantMessage({
        content: guidance.content,
        steps: guidance.steps,
        link: guidance.link,
      });
    }

    // Suppliers and buyers both have a verified tool set behind the assistant
    // endpoint, so both go there. getUserRole already lowercases and maps the
    // cookie's wording — 'owner' means buyer, 'suppliers' means supplier — so
    // this must compare against those mapped values, not the raw cookie.
    const role = this.getUserRole();
    const roleHasAssistantTools =
      role === 'supplier' || role === 'buyer' || role === 'admin';

    if (roleHasAssistantTools) {
      const dataQuestion = promptId ? this.dataActionQuestions[promptId] : undefined;
      return this.askTheAssistant(dataQuestion || question || 'Give me a summary of my business.');
    }

    // Anything else — an unrecognised role — keeps the pre-assistant behaviour
    // rather than being handed tools that were never scoped for it.
    return this.askLegacyEndpoint(promptId, question);
  }

  /**
   * Says whether a prompt asks for RFQ predictions, and which kind.
   *
   * Buyer-only: the predictions come from the buyer's own PO history. A typed
   * question counts when it mentions predictions, forecasts or suggested RFQs;
   * mentioning a vessel or ship picks the vessel-wise view.
   */
  private resolvePredictionKind(promptId?: string, question?: string): 'product' | 'vessel' | undefined {
    if (this.getUserRole() !== 'buyer') return undefined;
    if (promptId === 'qa-product-rfq-predictions') return 'product';
    if (promptId === 'qa-vessel-product-predictions') return 'vessel';
    if (promptId || !question) return undefined;

    if (!/\b(predict(ion|ions|ed)?|forecasts?|suggested\s+rfqs?)\b/i.test(question)) return undefined;
    return /\b(vessels?|ships?)\b|vessel-wise/i.test(question) ? 'vessel' : 'product';
  }

  /**
   * Fetches product-wise or vessel-wise RFQ predictions — the same call the
   * Suggested RFQs screen (operations/suggested-rfq) makes — and turns them
   * into a message the panel can draw as a list.
   */
  private async buildRfqPredictionsMessage(kind: 'product' | 'vessel'): Promise<ChatbotMessage> {
    const link = { label: 'Open Suggested RFQs', route: ['/operations/suggested-rfq'] };
    const noun = kind === 'vessel' ? 'vessel-wise product' : 'product';

    try {
      const response: any = await this.webApiService.postRequest('/rfq/fetchSuggestedRfqByBuyerId', {
        id: null,
        searchParams: '',
        docStatus: null,
        fromDate: null,
        toDate: null,
        limit: 10,
        offset: 0,
        isActive: 1,
        operation: kind === 'vessel' ? 'vessel-wise-product-forcast' : 'general-product-forcast',
      });

      const rawRows: any[] = Array.isArray(response?.rows) ? response.rows : Array.isArray(response) ? response : [];
      const totalRecords = typeof response?.totalElements === 'number' ? response.totalElements : rawRows.length;

      const predictedRows = rawRows.filter(
        (row) =>
          row.predicted_next_requirement !== null &&
          row.predicted_next_requirement !== undefined &&
          row.predicted_next_requirement !== ''
      );
      

      if (!predictedRows.length) {
        return this.buildAssistantMessage({
          content: `I have no ${noun} RFQ predictions for you yet. Predictions are built from your purchase order history, so they appear once you have repeat purchases.`,
          dataBacked: true,
          link,
        });
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const toNumber = (value: any) => (value === null || value === undefined || value === '' ? null : Number(value));

      const rows: RfqPredictionRow[] = predictedRows
      .filter((row) => row.predicted_next_requirement)
        .map((row) => {
          const predicted = row.predicted_next_requirement ? new Date(row.predicted_next_requirement) : null;
          return {
            productName: row.itemDescription || 'N/A',
            partNumber: row.partNumber || null,
            category: row.partType || null,
            unit: row.unit || null,
            vesselName: row.vesselName || null,
            vesselImoNumber: row.vesselImoNumber || null,
            purchases: toNumber(row.purchase_frequency),
            averageQuantity: toNumber(row.average_quantity),
            averageGapDays: toNumber(row.average_purchase_gap_days),
            predictedNextRequirement: row.predicted_next_requirement || null,
            overdue: !!predicted && !isNaN(predicted.getTime()) && predicted < today,
            frequency: row.frequency_classification || null,
            confidence: row.confidence_level || null,
          };
        })
        // Soonest requirement first; rows without a prediction go last.
        .sort((a, b) => {
          const aTime = a.predictedNextRequirement ? new Date(a.predictedNextRequirement).getTime() : Infinity;
          const bTime = b.predictedNextRequirement ? new Date(b.predictedNextRequirement).getTime() : Infinity;
          return aTime - bTime;
        });

      const overdueCount = rows.filter((row) => row.overdue).length;
      const shownNote = totalRecords > rows.length ? ` Showing ${rows.length} of ${totalRecords}, soonest first.` : '';
      const overdueNote = overdueCount
        ? ` ${overdueCount} ${overdueCount === 1 ? 'is' : 'are'} already past the predicted date — worth raising an RFQ now.`
        : '';

      return this.buildAssistantMessage({
        content: `Here are your ${noun} RFQ predictions, based on your purchase order history.${overdueNote}`,
        dataBacked: true,
        rfqPredictions: { kind, rows, totalRecords },
        link,
      });
    } catch {
      return this.buildAssistantMessage({
        content: `I could not load your ${noun} RFQ predictions just now. Please try again.`,
        tone: 'warning',
        canRetry: true,
        link,
      });
    }
  }

  /**
   * The pre-assistant behaviour, kept for buyers and admins.
   *
   * Their tool sets are not built yet, so their quick actions still call the
   * single-purpose endpoints. This will be deleted once those roles move over.
   */
  private async askLegacyEndpoint(promptId?: string, question?: string): Promise<ChatbotMessage> {
    const endpointsByPromptId: Record<string, { url: string; fallbackQuestion: string }> = {
      overview: { url: '/ai/dashboard-overview', fallbackQuestion: 'Give me an overview of my dashboard.' },
      notifications: { url: '/ai/notifications-overview', fallbackQuestion: 'Summarize my recent notifications.' },
      'qa-analyze-spend': { url: '/ai/spending-overview', fallbackQuestion: 'Analyze my spending by category.' },
      'qa-evaluate-suppliers': { url: '/ai/supplier-evaluation', fallbackQuestion: 'Evaluate my suppliers.' },
    };

    const endpoint = promptId ? endpointsByPromptId[promptId] : undefined;

    // Free-typed question with no matching endpoint: refuse straight away if it
    // belongs to a different role's cycle, rather than letting the model answer
    // about something this user cannot do. (Convenience only — the backend
    // enforces role properly; this just saves a round trip and reads better.)
    if (!endpoint) {
      const crossRoleRefusal = this.detectCrossRoleIntent(question || '');
      if (crossRoleRefusal) {
        return crossRoleRefusal;
      }
    }

    try {
      const apiResponse: any = await this.webApiService.postRequest(
        endpoint ? endpoint.url : '/ai/chat',
        this.buildAiRequest(question || endpoint?.fallbackQuestion || 'Help me with this.', promptId),
      );
      const answer =
        apiResponse?.answer || apiResponse?.data?.answer || apiResponse?.data?.data?.answer;

      if (!answer) {
        throw new Error('empty answer');
      }

      return this.buildAssistantMessage({
        content: answer,
        cta: promptId === 'notifications' ? 'notifications' : undefined,
      });
    } catch {
      return this.buildAssistantMessage({
        content:
          'I could not reach the assistant just now. Your data is safe — please try again in a moment.',
        tone: 'warning',
        canRetry: true,
      });
    }
  }

  getUserRole(): string | undefined {
    const role = (this.cookieService.get('userRole') || '').toLowerCase();
    if (role === 'owner') return 'buyer';
    if (role === 'suppliers') return 'supplier';
    if (role === 'sa' || role === 'superadmin' || role === 'admin') return 'admin';
    return role || undefined;
  }

  /** Human-readable role label for the chatbot header badge. */
  getRoleLabel(): string {
    const r = this.getUserRole();
    if (r === 'buyer')    return 'Buyer';
    if (r === 'supplier') return 'Supplier';
    if (r === 'admin')    return 'Admin';
    return 'Guest';
  }
}
